const CENTRIFUGE_LARGE_TYPE_IDS = new Set(['6980', '5960', '5721', '5980']);

/**
 * Check if a device is a centrifuge-large (SpinPro 6 R / SpinPro 8 R / NG6 / NG8)
 */
export function isCentrifugeLarge(device?: { type?: string } | null): boolean {
  return !!device?.type && CENTRIFUGE_LARGE_TYPE_IDS.has(device.type);
}

/**
 * Resolve a shadow component's setPoint to a displayable string value.
 * The Component interface types setPoint as `string?`, but at runtime centrifuge-large
 * devices may send:
 *   - a number (e.g., 9 for Acceleration/Deceleration ramps)
 *   - an array of numbers (e.g., [600] for single-step, [600, 300] for multi-step programs)
 *   - a string
 *
 * For arrays, returns the first element as a string.
 */
export function resolveSetPointValue(setPoint: unknown): string | undefined {
  if (setPoint == null) return undefined;
  if (Array.isArray(setPoint)) {
    return setPoint.length > 0 ? String(setPoint[0]) : undefined;
  }
  return String(setPoint);
}

/**
 * Format seconds into a human-readable time string with units.
 * Uses hairspace (\u200A) between numbers and their units,
 * and regular space between parts.
 *
 * Examples:
 *   30\u200As
 *   10\u200Amin 30\u200As
 *   1\u200Ah 05\u200Amin 30\u200As
 *
 * Returns "-" if value is 0.
 */
export function formatSecondsToTime(totalSeconds: number): string {
  if (totalSeconds === 0) return '-';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const hs = '\u200A'; // hairspace

  if (hours > 0) {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${hours}${hs}h ${pad(minutes)}${hs}min ${pad(seconds)}${hs}s`;
  }
  if (minutes > 0) {
    return `${minutes}${hs}min ${seconds.toString().padStart(2, '0')}${hs}s`;
  }
  return `${seconds}${hs}s`;
}

/**
 * Format a duration value coming from centrifuge-large telemetry.
 * The device sends ISO 8601 durations like "PT3600S" or raw seconds.
 * Converts to hh:mm:ss / mm:ss / ss format.
 * Returns "-" if value is 0.
 */
export function formatCentrifugeLargeDuration(
  value: string | number | number[] | undefined,
  valueType?: string,
): string | undefined {
  if (value == null) return undefined;

  // If value is an array (step program setpoints), use the first element
  const singleValue = Array.isArray(value) ? value[0] : value;
  if (singleValue == null) return undefined;

  // Coerce to string
  const strValue = typeof singleValue === 'number' ? String(singleValue) : singleValue;

  // Check if this is an ISO 8601 duration like "PT3600S"
  const match = strValue.match(/PT([\d.]+)S/);
  if (match) {
    const seconds = Number.parseFloat(match[1]);
    if (Number.isNaN(seconds)) return strValue;
    return formatSecondsToTime(Math.round(seconds));
  }

  // Try to interpret as raw seconds (number)
  const seconds = Number.parseFloat(strValue);
  if (!Number.isNaN(seconds)) {
    return formatSecondsToTime(Math.round(seconds));
  }

  return strValue;
}
